/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 26-07-2026
 *
 * De simfeedback-engine: draait op basis van de model-cy algoritmische checks (feedbackloops,
 * beginwaarden) en houdt de gevonden feedback bij. Port van de ng1-class `Simfeedbackengine`
 * (`app/states/canvas/simfeedbackengine.js`, golf 8a van stap 8); de checklogica is
 * regel-voor-regel overgenomen. De triggers: `run` start de `SimulatieService` vlak vóór het tekenen
 * van een simulatie, en de reset (bij het sluiten van de simulatie en bij elk vers document) loopt
 * via `pasSimfeedbackStatusToe` van de samenwerk-baas. De `simfb`-actielogregel gaat via de
 * geregistreerde callback naar de `OpslagService` (golf 3c). De wire-/snapshotvorm van de
 * simfeedback-status stelt de samenwerk-baas samen (`haalSimfeedbackStand`/`pasSimfeedbackStatusToe`).
 */
import {computed, inject, Injectable, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import type {Core, NodeSingular} from 'cytoscape';
import {Sihwlog} from 'sihw-ngx';
import {elements} from 'dynalearn-common/lib/elements';
import {GlobalModelcanvasService} from '../model/global-modelcanvas.service';
import {DlModel} from '../model/dl-model';
import {ProjectVlaggenService} from '../../services/project-vlaggen.service';
import type {Feedbackherkomst} from '../../lib/feedbackherkomst';

/**
 * De feedbackcategorieën, in weergavevolgorde. `_I`-varianten zijn de loops op niveau 40+ (zonder I).
 */
export type SimfeedbackType =
    'feedback_neg' | 'feedback_pos' | 'feedback_neg_I' | 'feedback_pos_I' |
    'd_beginwaarde_missend' | 'd_beginwaarde_blokkerend' | 'exogeen_blokkerend' |
    'beginwaarde_toekennen' | 'beginwaarde_blokkerend' | 'i_heeft_beginwaarde_nodig';

/**
 * De volledige feedback: per categorie een lijst van te highlighten element-id-sets
 * (id's, niet de elementen zelf, i.v.m. samenwerken).
 */
export type SimfeedbackData = Record<SimfeedbackType, string[][]>;

/**
 * Eén simfeedback-melding voor een node (structureel gelijk aan `FeedbackMelding` van
 * `BaseFeedbackIconComponent`).
 */
export interface SimfeedbackMelding {
    type: SimfeedbackType;
    tekst: string;
    /**
     * Of deze melding er bíj kwam ten opzichte van de vórige simulatieronde, of uit de eerste
     * simulatie op dit model komt. Binnen één simulatie kan er niets veranderen — de uitslag wordt
     * één keer geschreven en een modelwijziging sluit de simulatie — dus dit is per definitie een
     * vergelijking over twee runs heen; zie `ontwerp-isnieuw.md` § 6.
     */
    isNieuw: boolean;
}

/**
 * Eén causale stap in de feedbackloop-detectie.
 */
interface CausalStap {
    id: string;
    from: string;
    to: string;
    type: string;
    sign: 1 | -1;
}

@Injectable({providedIn: 'root'})
export class SimfeedbackEngineService {

    private readonly log = new Sihwlog('info', 'SimfeedbackEngine');

    /**
     * Permanente eigenaar van de model-`cy`; per run vers opgehaald.
     */
    private readonly modelcanvas = inject(GlobalModelcanvasService);

    /**
     * Het modeldocument, voor het effectieve modelleerniveau.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De projectvlaggen-spiegel (feedb_feedbackloop/feedb_beginwaarden).
     */
    private readonly projectVlaggen = inject(ProjectVlaggenService);

    private readonly translateService = inject(TranslateService);

    /**
     * De actuele feedback; vervangen als geheel bij run/reset/setFeedback zodat het signal bumpt.
     */
    private readonly _feedback = signal<SimfeedbackData>(SimfeedbackEngineService.legeFeedback());

    /**
     * De feedback waarin de checks tijdens een run schrijven; na de run gaat hij het signal in.
     */
    private werkFeedback: SimfeedbackData = SimfeedbackEngineService.legeFeedback();

    /**
     * De `cy` waar de lopende run op werkt; gezet door `run()`.
     */
    private cy!: Core;

    /**
     * Cache van de per-node meldingenlijsten: stabiele arrayreferenties zolang de feedback niet
     * wijzigt (vervangt de ng1 `node.scratch('_simfeedbackCache')`).
     */
    private readonly meldingenCache = new Map<string, SimfeedbackMelding[]>();

    /**
     * Welke node in welke categorie een melding had bij de vorige uitslag, als `nodeId|type`;
     * `null` zolang er voor dit model nog geen simulatie gedraaid heeft — en dan is álles nieuw, zie
     * `bepaalNieuweMeldingen`.
     *
     * De ijklijn **overleeft het sluiten van de simulatie**: de vraag is wat er nieuw is ten opzichte
     * van de vórige simulatieronde. Zet je iets recht en draai je opnieuw, dan pulseert precies het
     * probleem dat erbij gekomen is. Alleen een ander model zet hem terug op `null`; zie
     * `wisIjklijn`.
     */
    private vorigeSleutels: Set<string> | null = null;

    /**
     * De sleutels die er bij de laatste uitslag bíj kwamen. Vers `Set` per uitslag, zodat de
     * feedbackbalk aan de identiteit kan hangen (zelfde patroon als bij de norm).
     */
    private readonly _nieuweMeldingen = signal<Set<string>>(new Set());

    /**
     * De actuele feedback, als readonly signal.
     */
    readonly feedback = this._feedback.asReadonly();

    readonly nieuweMeldingen = this._nieuweMeldingen.asReadonly();

    /**
     * Door de `OpslagService` geregistreerde callback voor de `simfb`-actielogregel na een run met
     * resultaat; die kent het model-id en de snapshots (golf 3c, patroon `registreerLogHelp` in de
     * NormService).
     */
    private logSimfbCallback: ((feedback: SimfeedbackData) => void) | null = null;

    /**
     * Het totale aantal beschikbare feedbackitems (vervangt de ng1 `_numBeschikbaar`-caching).
     */
    readonly numBeschikbaar = computed(() => Object.values(this._feedback()).reduce((sum, lijst) => sum + lijst.length, 0));

    /**
     * Of we feedback over feedbackloops zouden kunnen genereren.
     */
    readonly feedbackloopAan = computed(() =>
        this.projectVlaggen.feedbFeedbackloop() && this.dlModel.effectief_modelleerniveau >= 20 && this.dlModel.effectief_modelleerniveau < 50);

    /**
     * Of we feedback over beginwaarden zouden kunnen genereren.
     */
    readonly beginwaardenAan = computed(() =>
        this.projectVlaggen.feedbBeginwaarden() && this.dlModel.effectief_modelleerniveau >= 20 && this.dlModel.effectief_modelleerniveau < 50);

    /**
     * Of de engine überhaupt iets zou kunnen doen.
     */
    readonly engineAan = computed(() => this.feedbackloopAan() || this.beginwaardenAan());

    /**
     * Een verse, lege feedbackstructuur met alle categorieën.
     */
    private static legeFeedback(): SimfeedbackData {
        return {
            feedback_neg: [], //feedbackloop loopt negatief: zelfdovend
            feedback_pos: [], //feedbackloop loopt positief: zelfversterkend
            feedback_neg_I: [], //idem, zonder I (niveau 40+)
            feedback_pos_I: [], //idem, zonder I (niveau 40+)
            d_beginwaarde_missend: [], //een Q aan het begin van een causale keten heeft geen beginwaarde op de afgeleide
            d_beginwaarde_blokkerend: [], //een Q niet aan het begin van een causale keten heeft wel een beginwaarde op afgeleide
            exogeen_blokkerend: [], //een Q niet aan het begin van een causale keten heeft een beginwaarde op afgeleide door een exogeen
            beginwaarde_toekennen: [], //een Q met waardenbereik heeft geen directe of indirecte waardetoekenning op dat bereik
            beginwaarde_blokkerend: [], //een Q met waardenbereik heeft én direct én indirect een waardetoekenning op dat bereik
            i_heeft_beginwaarde_nodig: [] //een Q waar een I uitkomt moet een beginwaarde hebben
        };
    }

    /**
     * Registreert de callback waarmee de `OpslagService` de `simfb`-regel in het actielog zet.
     */
    registreerLogSimfb(callback: (feedback: SimfeedbackData) => void): void {
        this.logSimfbCallback = callback;
    }

    /**
     * Leegt de feedback (alle categorieën blijven bestaan). Gebeurt als de simulatie sluit en bij
     * elke hertekening: er valt dan niets te tonen, en dus ook niets te melden.
     *
     * De ijklijn blijft hierbij staan. Die hoort bij het model en niet bij één simulatieronde: wat
     * de vólgende run oplevert wordt afgezet tegen wat deze opleverde.
     */
    reset(): void {
        this.log.debug('Resetten Simfeedback');
        this._feedback.set(SimfeedbackEngineService.legeFeedback());
        this.meldingenCache.clear();
        this._nieuweMeldingen.set(new Set());
    }

    /**
     * Zet de ijklijn terug op "nog geen simulatie gedraaid". Voor een ánder model: de meldingen van
     * het vorige zeggen niets over wat hier nieuw is. Aangeroepen door `OpslagService.reset()`,
     * naast die van de norm en de bouwsuggesties.
     */
    wisIjklijn(): void {
        this.vorigeSleutels = null;
    }

    /**
     * Run alle checks die we hebben op de actuele model-cy. Er is geen hoofdflag: elke check kijkt
     * naar zijn eigen projectvlag.
     */
    run(): void {
        const cy = this.modelcanvas.getCy();
        if (!cy) {
            this.log.warn('SimfeedbackEngine run zonder model-cy');
            this.reset();
            return;
        }
        this.cy = cy;
        this.log.debug('Runnen Simfeedback');
        this.werkFeedback = SimfeedbackEngineService.legeFeedback();
        this.checkFeedbackloops();
        this.checkBeginwaarden();
        this._feedback.set(this.werkFeedback);
        this.meldingenCache.clear();
        this.bepaalNieuweMeldingen();
        this.log.debug('[simfeedback] Simfeedback', this._feedback());
        //een run met resultaat hoort in het actielog; `setFeedback` (volger) logt bewust niet
        if (this.numBeschikbaar() > 0) {
            this.logSimfbCallback?.(this._feedback());
        }
    }

    /**
     * Extern zetten van onze feedback: van een leider in samenwerking, of van een replaystap.
     *
     * Die twee verschillen in één ding. Een leiderstand is een verse simulatieronde en verschuift dus
     * de ijklijn van "wat is hier nieuw". Een teruggehaalde stand is dat niet: een replaystap toont
     * een oude ronde, meldt niets nieuws, en laat de ijklijn staan waar hij stond — anders zou de
     * eerstvolgende echte ronde zich tegen een teruggebladerde stand meten.
     * @param feedback de simfeedback-uitslag
     * @param [herkomst] waar hij vandaan komt
     */
    setFeedback(feedback: SimfeedbackData, herkomst: Feedbackherkomst = 'vanLeider'): void {
        this._feedback.set(structuredClone(feedback));
        this.meldingenCache.clear();
        if (herkomst === 'teruggehaald') {
            this._nieuweMeldingen.set(new Set());
            return;
        }
        this.bepaalNieuweMeldingen();
    }

    /**
     * De simfeedback-meldingen voor één node: per categorie één melding als de node in minstens
     * één highlight-set van die categorie voorkomt. Stabiele arrayreferentie per node zolang de
     * feedback niet wijzigt (van belang voor de OnPush-`ngOnChanges` van `dl-simfeedback`).
     */
    getMeldingenVoorNode(node: NodeSingular): SimfeedbackMelding[] {
        const feedback = this._feedback(); //ook lezen bij een cache-hit: dit registreert de signal-dependency
        const nodeId = node.id();
        const cached = this.meldingenCache.get(nodeId);
        if (cached) {
            return cached;
        }
        const nieuw = this._nieuweMeldingen();
        const res: SimfeedbackMelding[] = [];
        for (const fbtype of Object.keys(feedback) as SimfeedbackType[]) {
            if (feedback[fbtype].some(ids => ids.includes(nodeId))) {
                res.push({
                    type: fbtype,
                    tekst: this.translateService.instant('FEEDBACK.SIM.' + fbtype),
                    isNieuw: nieuw.has(`${nodeId}|${fbtype}`)
                });
            }
        }
        this.meldingenCache.set(nodeId, res);
        return res;
    }

    /**
     * Bepaalt welke meldingen er bíj kwamen ten opzichte van de vorige simulatieronde.
     *
     * **Zonder voorganger is hier álles nieuw**, en dat is precies andersom dan bij de norm en de
     * bouwsuggesties (besluit gebruiker 10-09-2026). Daar staat de ijklijn op leeg omdat er een model
     * geopend is: die fouten en suggesties stonden er al, je hebt ze alleen nog niet gezien. Hier
     * staat hij op leeg omdat je voor het eerst een simulatie draait, en dan is de uitslag de verse
     * uitkomst van een handeling die je zojuist deed — vergelijkbaar met de eerste fout die je in een
     * model maakt. Dat pulseert.
     */
    private bepaalNieuweMeldingen(): void {
        const feedback = this._feedback();
        const eersteRonde = this.vorigeSleutels === null;
        const sleutels = new Set<string>();
        const nieuw = new Set<string>();
        for (const fbtype of Object.keys(feedback) as SimfeedbackType[]) {
            for (const ids of feedback[fbtype]) {
                for (const nodeId of ids) {
                    const sleutel = `${nodeId}|${fbtype}`;
                    sleutels.add(sleutel);
                    if (eersteRonde || !this.vorigeSleutels!.has(sleutel)) {
                        nieuw.add(sleutel);
                    }
                }
            }
        }
        this.vorigeSleutels = sleutels;
        this._nieuweMeldingen.set(nieuw);
    }

    /* ///////////////////////// Checks voor het vinden van feedback ///////////////////////// */

    /**
     * Check op feedbackloops: +/- causals die in een rondje gaan.
     */
    private checkFeedbackloops(): void {
        //juiste modelleerniveau?
        if (!this.feedbackloopAan()) {
            this.log.debug(`[simfeedback] check feedbackloops uitgeschakeld`);
            return;
        }
        //1: doe alle proportionalities
        //relaties zijn nodes met een paar edges ertussen

        //LET OP: alle conditionals uitgesloten, en ook multiply en division
        const selector = this.selectorForTypes(['proportionality_positive', 'proportionality_negative']);
        this.log.debug(`[simfeedback] selector`, selector);
        //we maken wat handige dataobjecten
        const causals: CausalStap[] = [];
        for (const el of this.cy.nodes(selector).toArray()) {
            const t = el.data('type') as string;
            causals.push({
                id: el.id(), //niet het element zelf, ivm samenwerking
                from: el.data('from') as string,
                to: el.data('to') as string,
                type: t,
                sign: ['proportionality_positive'].includes(t) ? 1 : -1 //vermenigvuldigbaar
            });
        }

        const gedaan: CausalStap[] = []; //causals die al als startpunt gedaan zijn
        const loops: Array<{route: CausalStap[]; sign: number; type: 'pos' | 'neg'}> = [];
        //we lopen over elke causal. De causals die we al bekeken hebben (als startpunt) hoeven niet meer in
        //andere rondjes voor te komen, want dat rondje kennen we dan al gegarandeerd
        //de routes die we vinden moeten dan ook echt vanaf deze causal.
        //zelfs als we tussendoor een andere route vinden
        for (const causal of causals) {
            //we maken een agenda voor het uitbreiden van de loop
            //die eindigt als we een object dubbel hebben
            const agenda: CausalStap[][] = [[causal]];
            while (agenda.length) {
                //uitbreiden: vind de volgende stap van de voorste
                const route = agenda.shift() as CausalStap[]; //voorste eraf
                const tail = route[route.length - 1];
                //vind alle causals die vertrekken vanaf ons eindpunt
                for (const volgende of causals.filter(cv => cv.from === tail.to)) {
                    //is dit een al uitgewerkte stap?
                    if (gedaan.includes(volgende)) {
                        continue; //dit rondje kennen we dan al, als er een rondje is (dan waren we hoe dan ook langs alle causals daarna gekomen)
                    }
                    if (volgende === route[0]) {
                        //LOOP!!
                        const sign = route.reduce((s, stap) => s * stap.sign, 1);
                        loops.push({
                            route: route,
                            sign: sign,
                            type: (sign > 0) ? 'pos' : 'neg' //voor het gemak
                        });

                        //skip expliciet rondjes die we vinden maar die niet vanaf het begin zijn
                        //en skip ook als volgende.to al ergens in de route zit
                    } else if ((!route.includes(volgende)) && (!route.some(el => volgende.from === el.from))) {
                        //okee, uitbreiden maar
                        agenda.push([...route, volgende]); //loopt dood of
                    }
                }
            }
            //toekomstige rondjes met de causal die we nu hebben uitgewerkt.
            //zijn gegarandeerd gedraaide rondjes, dus dezelfde:
            gedaan.push(causal);
        }
        //even uitwerken
        const feedback_neg = loops.filter(l => l.type === 'neg').map(l => l.route.map(stap => stap.id));
        const feedback_pos = loops.filter(l => l.type === 'pos').map(l => l.route.map(stap => stap.id));
        if (this.dlModel.effectief_modelleerniveau < 40) {
            this.werkFeedback.feedback_neg = feedback_neg;
            this.werkFeedback.feedback_pos = feedback_pos;
        } else {
            this.werkFeedback.feedback_neg_I = feedback_neg;
            this.werkFeedback.feedback_pos_I = feedback_pos;
        }
    }

    /**
     * Check of elke Q wel een beginwaarde heeft + ook een d-waarde als hij een afgeleide heeft.
     */
    private checkBeginwaarden(): void {
        //staat dit aan en juiste modelleerniveau?
        if (!this.beginwaardenAan()) {
            this.log.debug(`[simfeedback] check beginwaarden uitgeschakeld`);
            return;
        }
        //het is veel eenvoudiger geworden. Elke Q moet een d-value hebben + een Q-value hebben (als er een QS is)

        //even wat informatie verzamelen
        for (const Q of this.cy.nodes(this.selectorForTypes(['quantity'])).toArray()) {
            const Q_id = Q.id();
            //D-value
            //lokaal?
            const dvalue = this.vindChild(Q, 'derivative_value');
            const exo = this.vindChild(Q, 'q_exo');
            let extern = this.dBeginwaardeExtern(Q);

            //bij een te verwijderen waarde alleen de Q: de waarde zelf is niet in elke state zichtbaar
            if (extern && dvalue) {
                this.pushFeedbackAlsNiet('d_beginwaarde_blokkerend', Q_id, [Q_id]);
            }
            if (extern && exo) {
                this.pushFeedbackAlsNiet('exogeen_blokkerend', Q_id, [Q_id]);
            }
            if (!(extern || dvalue || exo)) {
                //er is niets
                this.pushFeedbackAlsNiet('d_beginwaarde_missend', Q_id, [Q_id]);
            }

            //nu de values, maar alleen als er een QS is
            const QS = this.vindChild(Q, 'quantity_space');
            if (QS) {
                const value = this.vindChild(Q, 'quantity_value') || this.vindChild(Q, 'quantity_allvalues');
                extern = this.qBeginwaardeExtern(Q);
                if (value && extern) {
                    //dubbele beginwaarde
                    this.pushFeedbackAlsNiet('beginwaarde_blokkerend', Q_id, [Q_id]);
                } else if (!(value || extern)) {
                    //geen beginwaarde
                    this.pushFeedbackAlsNiet('beginwaarde_toekennen', Q_id, [Q_id]);
                }
            }
        }
    }

    /**
     * Helper bij checkBeginwaarden, voor recursie. True als er een externe bron voor de beginwaarde
     * van de afgeleide is.
     * @param Q Quantity
     * @param skip Ids van Q's waarvan we al kijken of de waarde extern is (loop-stop)
     */
    private dBeginwaardeExtern(Q: NodeSingular, skip?: string[]): boolean {
        skip = skip || [];
        const Q_id = Q.id();
        const dqs = this.vindChild(Q, 'derivative') as NodeSingular;
        const dqs_id = dqs.id();

        //we gaan het even uitzoeken
        //causals naar de Q
        if (this.cy.nodes(this.selectorForTypes(['causal'], true)).is(`[to = '${Q_id}']`)) {
            return true;
        }
        //gerichte correspondenties naar de d
        if (this.cy.nodes(this.selectorForTypes(['correspondence_dqs_directed', 'correspondence_dqs_directed_reverse'])).is(`[to = '${dqs_id}']`)) {
            return true;
        }
        //exacte uitkomst van een calculus
        if (this.cy.nodes(this.selectorForTypes(['d_ineq_eq'])).toArray().some(ineq =>
            (ineq.data('to') === dqs_id && elements.isSubtypeOf(this.cy.$id(ineq.data('from') as string).data('type') as string, 'd_calc')) ||
            (ineq.data('from') === dqs_id && elements.isSubtypeOf(this.cy.$id(ineq.data('to') as string).data('type') as string, 'd_calc'))
        )) {
            return true;
        }
        //bidirectionele correspondenties: hiervoor geldt dat de andere kant zelf ook weer een waarde moet
        //krijgen (en niet van deze)
        for (const corr of this.cy.nodes(this.selectorForTypes(['correspondence_dqs_normal', 'correspondence_dqs_reverse'])).filter(`[from = '${dqs_id}'],[to = '${dqs_id}']`).toArray()) {
            const andere_id = (corr.data('from') === dqs_id ? corr.data('to') : corr.data('from')) as string;
            const andereQ = this.getQuantity(this.cy.$id(andere_id));
            if (!andereQ || skip.includes(andereQ.id())) {
                continue; //stop de loop, dit gaat hem niet worden
            }
            //heeft die een waarde?
            if (this.vindChild(andereQ, 'derivative_value') || this.vindChild(andereQ, 'q_exo')) {
                return true; //externe waarde voor deze
            }
            //anders moet deze zelf een externe waarde hebben, maar niet via deze
            skip.push(Q_id);
            if (this.dBeginwaardeExtern(andereQ, skip)) {
                return true;
            }
        }
        return false; //niet gevonden
    }

    /**
     * Helper bij checkBeginwaarden, voor recursie. True als er een externe bron voor de beginwaarde
     * van de quantity zelf is.
     * @param Q Quantity
     * @param skip Ids van Q's waarvan we al kijken of de waarde extern is (loop-stop)
     */
    private qBeginwaardeExtern(Q: NodeSingular, skip?: string[]): boolean {
        skip = skip || [];
        const Q_id = Q.id();

        //checkBeginwaarden kijkt alleen naar Q's met een QS
        //wij ook naar Q's zonder QS, maar natuurlijk minder checks
        const QS = this.vindChild(Q, 'quantity_space');
        const QS_id = QS ? QS.id() : null;

        //gerichte correspondenties
        if (QS_id && this.cy.nodes(this.selectorForTypes(['correspondence_qs_directed', 'correspondence_qs_directed_reverse'], true)).is(`[to = '${QS_id}']`)) {
            return true;
        }
        //exacte uitkomst van een calculus
        if (this.cy.nodes(this.selectorForTypes(['q_ineq_eq'])).toArray().some(ineq =>
            (ineq.data('to') === Q_id && elements.isSubtypeOf(this.cy.$id(ineq.data('from') as string).data('type') as string, 'q_calc')) ||
            (ineq.data('from') === Q_id && elements.isSubtypeOf(this.cy.$id(ineq.data('to') as string).data('type') as string, 'q_calc'))
        )) {
            return true;
        }

        //bidirectionele correspondenties: hiervoor geldt dat de andere kant zelf ook weer een waarde moet
        //krijgen (en niet van deze)
        if (QS_id) {
            for (const corr of this.cy.nodes(this.selectorForTypes(['correspondence_qs_normal', 'correspondence_qs_reverse'])).filter(`[from = '${QS_id}'],[to = '${QS_id}']`).toArray()) {
                const andere_id = (corr.data('from') === QS_id ? corr.data('to') : corr.data('from')) as string;
                const andereQ = this.getQuantity(this.cy.$id(andere_id));
                if (!andereQ || skip.includes(andereQ.id())) {
                    continue; //deze wordt al gecheckt via een loop
                }
                //heeft die een waarde?
                if (this.vindChild(andereQ, 'quantity_value') || this.vindChild(andereQ, 'quantity_allvalues')) {
                    return true; //externe waarde voor deze
                }
                //anders moet deze zelf een externe waarde hebben, maar niet via de correspondentie
                skip.push(Q_id);
                if (this.qBeginwaardeExtern(andereQ, skip)) {
                    return true;
                }
            }
        }

        //een specifieke waarde in de QS wordt gezet
        if (QS_id) {
            return (this.cy.nodes(
                this.selectorForTypes(['quantity_space_element'], true)
            ).filter(`[qspace = "${QS_id}"]`).toArray() as NodeSingular[]).some(
                qsEl => this.qsElKrijgtWaarde(qsEl, [], true));
        }

        return false;
    }

    /**
     * Geeft true als het gegeven QS-element op de een of andere manier direct of extern een waarde
     * krijgt: er zit een waarde bij het element, of hij komt binnen via een correspondentie.
     * @param QSEl
     * @param skip
     * @param skipDirect als true dan geen directe
     */
    private qsElKrijgtWaarde(QSEl: NodeSingular, skip: string[] | null, skipDirect?: boolean): boolean {
        skip = skip || [];
        //direct?
        if ((!skipDirect) && (QSEl as unknown as {outElements: (selector: string) => {nonempty: () => boolean}}).outElements('[type = "quantity_value"]').nonempty()) {
            return true; //deze heeft een waarde
        }
        //gerichte cor of gewone cor met dit element aan de TO
        for (const cor of
            this.cy.nodes(
                this.selectorForTypes(['correspondence_qv'], true)
            ).filter(`[to = '${QSEl.id()}']`).toArray()) {
            //deze gaat dus naar dit element
            //we gaan dus door met de andere kant
            const andere = this.cy.$id(cor.data('from') as string);
            //skippen?
            if (skip.includes(andere.id())) {
                //al gedaan, cycle
                continue;
            }
            return this.qsElKrijgtWaarde(andere, [QSEl.id(), ...skip]);
        }

        //en bij bidir de andere kant op (maar waarde komt dan niet van deze)
        for (const cor of
            this.cy.nodes(
                this.selectorForTypes(['correspondence_qv_normal'], true)
            ).filter(`[from = '${QSEl.id()}']`).toArray()) {
            //deze gaat dus naar dit element
            //we gaan dus door met de andere kant
            const andere = this.cy.$id(cor.data('to') as string);
            //skippen?
            if (skip.includes(andere.id())) {
                //al gedaan, cycle
                continue;
            }
            return this.qsElKrijgtWaarde(andere, [QSEl.id(), ...skip]);
        }
        return false;
    }

    /* //// interne helpers //// */

    /**
     * Vind het eerste kindelement van een bepaald type (of subtype ervan). Recursief.
     */
    private vindChild(element: NodeSingular, type: string): NodeSingular | null {
        for (const childId of ((element.data('childIds') as string[] | undefined) || [])) {
            const child = this.cy.$id(childId);
            if (elements.isSubtypeOf(child.data('type') as string, type)) {
                return child; //gevonden
            }
            //anders de recursie in
            const subchild = this.vindChild(child, type);
            if (subchild) {
                return subchild; //gevonden
            }
        }
        //als we hier komen: niets gevonden
        return null;
    }

    /**
     * De quantity waar deze node (recursief) onder hangt, of null.
     */
    private getQuantity(node: NodeSingular): NodeSingular | null {
        if (node.data('type') === 'quantity') {
            return node;
        }
        const parentId = node.data('parentId') as string | undefined;
        return parentId ? this.getQuantity(this.cy.$id(parentId)) : null;
    }

    /**
     * Maak een selector voor elementen van bepaalde types.
     * @param types
     * @param subtypes ook alle subtypes meenemen
     * @param ookConditional ook conditionele elementen meenemen
     */
    private selectorForTypes(types: string[], subtypes?: boolean, ookConditional?: boolean): string {
        const maptypes = [...types];
        if (subtypes) {
            for (const type of types) {
                maptypes.push(...elements.alleSubs(type));
            }
        }
        return maptypes.map(t => `[type="${t}"]${ookConditional ? '' : '[!condtag]'}`).join(',');
    }

    /**
     * Push een nieuwe array van id's in een bepaalde feedbackcategorie, tenzij een gegeven element
     * er al in zit.
     */
    private pushFeedbackAlsNiet(feedbackkey: SimfeedbackType, checkElementId: string, highlightArray: string[]): void {
        if (!this.werkFeedback[feedbackkey].some(hla => hla.includes(checkElementId))) {
            this.werkFeedback[feedbackkey].push(highlightArray);
        }
    }
}
